import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * ギフトボックスの中身を選ばせるコンポーネント。
 *
 * コレクションの各商品に数量セレクタを並べ、合計が規定数に達するまで
 * カート追加ボタンを無効化する。選択内容は表示用テキストと機械可読 JSON の
 * 2つの hidden input に書き出し、商品フォームの line item properties として送る。
 *
 * 1商品に複数配置できる（例: ミックスはループ用とロゼル用の2つ）。
 * その場合はすべてが充足して初めてカートに追加できる。
 *
 * ボタンが押せない理由が分かるよう、揃うまでの間は次の3つを出す。
 * - 購入ボタンのすぐ上に「タルトループをあと2個お選びください」の案内と「中身を選ぶ」
 * - Shop Pay などの加速チェックアウトを薄く表示（CSS。ブロックのスタイルを参照）
 * - 固定バーのボタンを「中身をお選びください」にし、押すと未選択のピッカーへ移動
 *
 * 単品はオンラインストアで販売しないため、各行の「詳しく見る」で味の紹介をモーダルに出す。
 * 中身は行ごとの template（snippets/product-flavor-detail.liquid）を開くときに差し込む。
 *
 * @typedef {object} GiftBoxPickerRefs
 * @property {HTMLElement} root - data 属性を持つルート要素。
 * @property {HTMLElement[]} counts - 各商品の選択数を表示する output 要素。
 * @property {HTMLButtonElement[]} plusButtons - 増やすボタン。
 * @property {HTMLButtonElement[]} minusButtons - 減らすボタン。
 * @property {HTMLElement} counterCurrent - 合計数の表示要素。
 * @property {HTMLInputElement} textInput - 表示用テキストの hidden input。
 * @property {HTMLInputElement} jsonInput - 機械可読 JSON の hidden input。
 * @property {HTMLElement} liveRegion - スクリーンリーダー向けの通知領域。
 * @property {HTMLElement} shortage - 在庫不足の案内。選べる総数が規定数に届かないときだけ出す。
 * @property {HTMLTemplateElement[]} detailTemplates - 各商品の味の紹介。
 * @property {HTMLDialogElement} detailDialog - 味の紹介を出すモーダル。
 * @property {HTMLElement} detailBody - モーダルの中で味の紹介を差し込む場所。
 *
 * @extends Component<GiftBoxPickerRefs>
 */
class GiftBoxPickerComponent extends Component {
  /** @type {AbortController | null} */
  #detailAbortController = null;

  /** @type {HTMLElement | null} */
  #detailOpener = null;

  connectedCallback() {
    super.connectedCallback();

    // コレクション未設定時は中身が描画されないため、何もしない
    if (!this.refs.root) return;

    this.sync();

    // Esc キーやフォームの送信でも閉じられるため、閉じた後の片付けは close イベントで行う。
    // 再接続で購読が重ならないよう、前の購読を外してから付け直す
    this.#detailAbortController?.abort();
    this.#detailAbortController = new AbortController();
    this.refs.detailDialog?.addEventListener('close', () => this.#handleDetailClosed(), {
      signal: this.#detailAbortController.signal,
    });

    // 味の紹介は開くたびに差し込み直すため、サムネイルのクリックは差し込み先でまとめて受け取る
    this.refs.detailBody?.addEventListener('click', (event) => this.#handleDetailThumbClick(event), {
      signal: this.#detailAbortController.signal,
    });

    // 商品フォームのカスタム要素がまだ upgrade されていないと disable() を持たず、
    // 未選択のままボタンが押せてしまう。定義された後にもう一度かける
    customElements.whenDefined('add-to-cart-component').then(() => {
      if (this.isConnected) this.#updateAddToCartState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#detailAbortController?.abort();
    this.#detailAbortController = null;

    const { detailDialog } = this.refs;
    if (detailDialog) unlockScroll(detailDialog);
  }

  /**
   * 「詳しく見る」のクリック。その行の味の紹介をモーダルに差し込んで開く。
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  openDetail(event) {
    const button = event.target.closest('[data-index]');
    if (!(button instanceof HTMLElement)) return;

    const { detailDialog, detailBody } = this.refs;
    const template = this.#toArray(this.refs.detailTemplates)[Number(button.dataset.index)];
    if (!detailDialog || !detailBody || !(template instanceof HTMLTemplateElement)) return;

    detailBody.replaceChildren(template.content.cloneNode(true));
    detailDialog.scrollTop = 0;

    this.#detailOpener = button;

    if (!detailDialog.open) {
      lockScroll(detailDialog);
      detailDialog.showModal();
    }
  }

  /**
   * 閉じるボタンのクリック。
   */
  closeDetail() {
    this.refs.detailDialog?.close();
  }

  /**
   * モーダルの外側（背景）のクリックで閉じる。
   * 背景をクリックすると dialog 要素そのものが target になるため、それだけを拾う。
   * @param {MouseEvent} event
   */
  handleDetailDialogClick(event) {
    const { detailDialog } = this.refs;
    if (!detailDialog || event.target !== detailDialog) return;

    const rect = detailDialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!inside) detailDialog.close();
  }

  /**
   * サムネイルのクリックで、大きな画像を選んだものに切り替える。
   * @param {MouseEvent} event
   */
  #handleDetailThumbClick(event) {
    if (!(event.target instanceof Element)) return;

    const thumb = event.target.closest('[data-flavor-thumb]');
    if (!(thumb instanceof HTMLElement)) return;

    const { detailBody } = this.refs;
    if (!detailBody) return;

    const index = thumb.dataset.flavorThumb;

    for (const image of detailBody.querySelectorAll('[data-flavor-image]')) {
      if (image instanceof HTMLElement) image.hidden = image.dataset.flavorImage !== index;
    }

    for (const other of detailBody.querySelectorAll('[data-flavor-thumb]')) {
      if (other === thumb) {
        other.setAttribute('aria-current', 'true');
      } else {
        other.removeAttribute('aria-current');
      }
    }
  }

  /**
   * モーダルが閉じた後の片付け。スクロールを戻し、押した「詳しく見る」へフォーカスを返す。
   */
  #handleDetailClosed() {
    const { detailDialog, detailBody } = this.refs;

    if (detailDialog) unlockScroll(detailDialog);
    detailBody?.replaceChildren();

    this.#detailOpener?.focus({ preventScroll: true });
    this.#detailOpener = null;
  }

  /**
   * 案内に出すグループ名（選択肢のコレクション名）。
   * @returns {string}
   */
  get groupLabel() {
    return this.refs.root?.dataset.groupLabel ?? '';
  }

  /**
   * 規定数まであと何個選ぶ必要があるか。
   * @returns {number}
   */
  get remaining() {
    return Math.max(this.requiredCount - this.total, 0);
  }

  /**
   * 見出しまでスクロールし、読み上げの起点をそこへ移す。
   */
  reveal() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    const header = this.querySelector('.gift-box-picker__header');
    if (header instanceof HTMLElement) header.focus({ preventScroll: true });
  }

  /**
   * 選ばせる個数。
   * @returns {number}
   */
  get requiredCount() {
    return Number(this.refs.root?.dataset.requiredCount) || 0;
  }

  /**
   * 商品自体が購入可能か。売り切れ商品のボタンを誤って有効化しないために使う。
   * @returns {boolean}
   */
  get productAvailable() {
    return this.refs.root?.dataset.productAvailable === 'true';
  }

  /**
   * 現在の選択数の合計。
   * @returns {number}
   */
  get total() {
    return this.#countElements.reduce((sum, element) => sum + this.#readCount(element), 0);
  }

  /**
   * 規定数を満たしているか。
   * @returns {boolean}
   */
  get isComplete() {
    // コレクション未設定のブロックは判定に含めない（充足扱い）
    if (!this.refs.root) return true;

    return this.requiredCount > 0 && this.total === this.requiredCount;
  }

  /**
   * 在庫の範囲で選べる総数。1つでも上限なしの商品があれば規定数は必ず満たせる。
   * @returns {number}
   */
  get selectableTotal() {
    return this.#countElements.reduce((sum, element) => sum + this.#readMax(element), 0);
  }

  /**
   * 在庫が足りず、規定数を揃えられない状態か。
   * @returns {boolean}
   */
  get isShortOfStock() {
    if (!this.refs.root) return false;

    return this.requiredCount > 0 && this.selectableTotal < this.requiredCount;
  }

  /**
   * 増減ボタンのクリック。ボタンの data 属性から対象と増減幅を読む。
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  handleStep(event) {
    const button = event.target.closest('[data-step]');
    if (!(button instanceof HTMLElement)) return;

    const step = Number(button.dataset.step);
    const index = Number(button.dataset.index);
    const element = this.#countElements[index];
    if (!element || !step) return;

    const next = this.#readCount(element) + step;
    const remaining = this.requiredCount - this.total;

    // 下限0、合計が規定数を超えない、その商品の在庫を超えない、の3つで丸める
    if (next < 0) return;
    if (step > 0 && remaining <= 0) return;
    if (step > 0 && next > this.#readMax(element)) return;

    element.textContent = String(next);
    this.sync();
  }

  /**
   * 表示・入力値・ボタン状態をまとめて現在の選択に合わせる。
   */
  sync() {
    const { counterCurrent, textInput, jsonInput, liveRegion } = this.refs;
    const total = this.total;
    const required = this.requiredCount;

    if (counterCurrent) counterCurrent.textContent = String(total);

    counterCurrent?.closest('.gift-box-picker__counter')?.classList.toggle(
      'gift-box-picker__counter--complete',
      total === required
    );

    // 規定数に達したか、その商品の在庫を使い切ったら増やせないようにする。
    // 減らす側は0でなければ常に有効
    for (const [index, button] of this.#plusButtons.entries()) {
      const element = this.#countElements[index];
      const reachedMax = element !== undefined && this.#readCount(element) >= this.#readMax(element);

      button.disabled = total >= required || reachedMax;
    }

    for (const [index, button] of this.#minusButtons.entries()) {
      const element = this.#countElements[index];
      button.disabled = !element || this.#readCount(element) === 0;
    }

    const selected = this.#selectedItems();

    if (textInput) textInput.value = this.#buildText(selected);
    if (jsonInput) jsonInput.value = selected.length > 0 ? this.#buildJson(selected) : '';

    if (liveRegion) liveRegion.textContent = `${total} / ${required}`;

    const { shortage } = this.refs;
    if (shortage) shortage.hidden = !this.isShortOfStock;

    this.#updateAddToCartState();
  }

  /**
   * 数量が1以上の商品だけを取り出す。
   * @returns {{ productId: number, variantId: number, title: string, quantity: number }[]}
   */
  #selectedItems() {
    return this.#countElements
      .map((element) => ({
        productId: Number(element.dataset.productId),
        variantId: Number(element.dataset.variantId),
        title: element.dataset.title ?? '',
        quantity: this.#readCount(element),
      }))
      .filter((item) => item.quantity > 0);
  }

  /**
   * カートや注文詳細に表示されるテキストを組み立てる。商品名は登録名をそのまま使う。
   * @param {{ title: string, quantity: number }[]} items
   * @returns {string}
   */
  #buildText(items) {
    return items.map((item) => `${item.title}×${item.quantity}`).join('、');
  }

  /**
   * 将来の在庫連動で使う機械可読データを組み立てる。
   * product_id は LeLAS の商品マスタと突合するためのキー。
   * @param {{ productId: number, variantId: number, title: string, quantity: number }[]} items
   * @returns {string}
   */
  #buildJson(items) {
    return JSON.stringify({
      required: this.requiredCount,
      items: items.map((item) => ({
        product_id: item.productId,
        variant_id: item.variantId,
        title: item.title,
        quantity: item.quantity,
      })),
    });
  }

  /**
   * 同じ商品ページ上のすべてのピッカーが充足したときだけカート追加を許可する。
   * テーマ標準の add-to-cart-component の API を使い、テーマ本体は改変しない。
   */
  #updateAddToCartState() {
    const scope = this.#scope;
    if (!scope) return;

    const pickers = /** @type {GiftBoxPickerComponent[]} */ (
      Array.from(scope.querySelectorAll('gift-box-picker-component'))
    );

    // まだ upgrade されていない要素は isComplete が undefined になる。
    // 充足扱いにすると未選択のままカートに入れられてしまうため、厳密に true だけを通す。
    const canAdd =
      this.productAvailable &&
      pickers.every((picker) => picker.isComplete === true && picker.isShortOfStock === false);

    const productForm = scope.querySelector('product-form-component');
    for (const container of productForm?.querySelectorAll('add-to-cart-component') ?? []) {
      if (canAdd) {
        /** @type {any} */ (container).enable?.();
      } else {
        /** @type {any} */ (container).disable?.();
      }
    }

    // 中身さえ揃えば買える状態。売り切れや在庫不足で揃えようがないときは案内を出さない
    const pending =
      !canAdd &&
      this.productAvailable &&
      pickers.every((picker) => picker.isShortOfStock === false);

    this.#updateHint(scope, productForm, pickers, pending);
    this.#updateStickyButton(scope, canAdd, pending);
  }

  /**
   * 購入ボタンの直前に、あと何を何個選べばよいかの案内を出す。
   *
   * ピッカーは購入ボタンより上に並ぶため、ピッカー内のカウンタはボタンの位置から
   * 見えない。案内の要素は同じ商品のピッカー間で1つを共有し、なければ作る。
   *
   * 購入ボタンのブロックの外に置くと、並びを作るグループの間隔（24px）が挟まって
   * ボタンと離れてしまうため、ブロックの内側の先頭に入れる。
   *
   * @param {Element} scope
   * @param {Element | null} productForm
   * @param {GiftBoxPickerComponent[]} pickers
   * @param {boolean} pending
   */
  #updateHint(scope, productForm, pickers, pending) {
    const buyButtons = productForm?.closest('.buy-buttons-block');
    if (!buyButtons) return;

    let hint = scope.querySelector('.gift-box-picker__hint');

    if (!(hint instanceof HTMLElement)) {
      if (!pending) return;

      hint = this.#createHint(scope);
      buyButtons.prepend(hint);
    }

    hint.hidden = !pending;
    if (!pending) return;

    const shortfalls = pickers
      .filter((picker) => picker.remaining > 0)
      .map((picker) => `${picker.groupLabel || '中身'}をあと${picker.remaining}個`);

    const text = hint.querySelector('.gift-box-picker__hint-text');
    if (text) text.textContent = `${shortfalls.join('・')}お選びください`;
  }

  /**
   * 案内の要素を作る。ピッカーの外に置くため、クリックは直接購読する。
   * @param {Element} scope
   * @returns {HTMLElement}
   */
  #createHint(scope) {
    const hint = document.createElement('div');
    hint.className = 'gift-box-picker__hint';

    const text = document.createElement('p');
    text.className = 'gift-box-picker__hint-text';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gift-box-picker__hint-button';
    button.textContent = '中身を選ぶ';
    button.addEventListener('click', () => revealIncompletePicker(scope));

    hint.append(text, button);

    return hint;
  }

  /**
   * 固定バーのボタンの状態を合わせる。
   *
   * 中身が揃うまでは押せるままにして「中身をお選びください」と表示し、押されたら
   * 未選択のピッカーへ移動する（クリックの横取りはファイル末尾の購読を参照）。
   * 無効にすると押しても何も起きず、理由も分からないため。
   *
   * @param {Element} scope
   * @param {boolean} canAdd
   * @param {boolean} pending
   */
  #updateStickyButton(scope, canAdd, pending) {
    const button = scope.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const label = button.querySelector('.add-to-cart-text__content > span');

    if (label && button.dataset.giftBoxLabel === undefined) {
      button.dataset.giftBoxLabel = label.textContent ?? '';
    }

    if (pending) {
      button.dataset.giftBoxPending = 'true';
      if (label) label.textContent = '中身をお選びください';
    } else {
      delete button.dataset.giftBoxPending;
      if (label) label.textContent = button.dataset.giftBoxLabel ?? label.textContent;
    }

    button.disabled = !canAdd && !pending;
  }

  /**
   * 同じ商品の要素だけを対象にするための探索範囲。
   * @returns {Element | null}
   */
  get #scope() {
    return this.closest('.shopify-section');
  }

  /** @returns {HTMLElement[]} */
  get #countElements() {
    return this.#toArray(this.refs.counts);
  }

  /** @returns {HTMLButtonElement[]} */
  get #plusButtons() {
    return /** @type {HTMLButtonElement[]} */ (this.#toArray(this.refs.plusButtons));
  }

  /** @returns {HTMLButtonElement[]} */
  get #minusButtons() {
    return /** @type {HTMLButtonElement[]} */ (this.#toArray(this.refs.minusButtons));
  }

  /**
   * ref は要素が1つだけのとき配列にならないため、常に配列として扱えるようにする。
   * @param {Element | Element[] | undefined} value
   * @returns {any[]}
   */
  #toArray(value) {
    if (!value) return [];

    return Array.isArray(value) ? value : [value];
  }

  /**
   * その商品を選べる上限。
   *
   * 在庫を追跡していない商品は Liquid が -1 を出すため、上限なしとして扱う。
   * 属性が無い場合も -1 と同じ扱いにする。追跡していない状態で選べなくなるより、
   * 上限を効かせ損ねるほうが害が小さいため。
   *
   * @param {HTMLElement} element
   * @returns {number}
   */
  #readMax(element) {
    const raw = element.dataset.maxQuantity;
    if (raw === undefined) return Infinity;

    const max = Number(raw);

    return max < 0 ? Infinity : max;
  }

  /**
   * @param {HTMLElement} element
   * @returns {number}
   */
  #readCount(element) {
    return Number(element.textContent?.trim()) || 0;
  }
}

/**
 * 同じ商品のピッカーのうち、まだ揃っていない最初のものへ移動する。
 * @param {Element} scope
 */
function revealIncompletePicker(scope) {
  const pickers = /** @type {GiftBoxPickerComponent[]} */ (
    Array.from(scope.querySelectorAll('gift-box-picker-component'))
  );

  pickers.find((picker) => picker.isComplete !== true)?.reveal?.();
}

if (!customElements.get('gift-box-picker-component')) {
  customElements.define('gift-box-picker-component', GiftBoxPickerComponent);

  // 固定バーのボタンは、テーマの仕組み（document の capture）で押下が処理され、
  // 本体ボタンの click() とカートへ飛ぶ演出が走る。中身が揃っていない間はそれより先に
  // window の capture で受け取り、未選択のピッカーへの移動に差し替える
  window.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('sticky-add-to-cart [data-gift-box-pending]');
      if (!button) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const scope = button.closest('.shopify-section');
      if (scope) revealIncompletePicker(scope);
    },
    { capture: true }
  );
}
